import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, LayoutDashboard, FileText, Users, Globe2, Layers, Code2, Music,
  Boxes, Coins, Download, BarChart3, Play, Pencil, Loader2, Star, Heart, Rocket,
  CheckCircle2, Circle, Gem, Wand2, Sparkles, RefreshCw, Save, Copy, Upload, History, Trash2, Clapperboard,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Spinner, EmptyState } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PublishTemplateDialog } from "@/components/PublishTemplateDialog";
import { LicenceProvenanceCard } from "@/components/LicenceProvenanceCard";
import { toast } from "sonner";

const SECTIONS = [
  ["overview", "Overview", LayoutDashboard],
  ["gdd", "Design Doc", FileText],
  ["characters", "Characters", Users],
  ["world", "World", Globe2],
  ["levels", "Levels", Layers],
  ["scripts", "Logic & Scripts", Code2],
  ["audio", "Audio", Music],
  ["assets", "Assets", Boxes],
  ["economy", "Economy", Coins],
  ["exports", "Exports", Download],
  ["saves", "Project & Saves", Save],
  ["analytics", "Analytics", BarChart3],
];

export default function GameProject() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [sec, setSec] = useState("overview");

  useEffect(() => { api.get(`/games/${id}/project`).then((r) => setP(r.data)).catch(() => setP(false)); }, [id]);
  if (p === false) return <div className="p-8"><EmptyState emoji="🔍" title="Project not found" action={<Button onClick={() => navigate(-1)} className="rounded-full">Go back</Button>} /></div>;
  if (!p) return <Spinner />;
  const o = p.overview;

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="game-project">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] mb-4" data-testid="project-back"><ArrowLeft className="w-4 h-4" /> Back</button>

      {/* Header */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mb-5 flex items-center gap-4 flex-wrap">
        <div className="w-16 h-16 rounded-2xl grid place-items-center text-3xl bg-gradient-to-br from-[#1f6feb] to-[#7c3aed] shrink-0 overflow-hidden">
          {o.cover_image ? <img src={`${process.env.REACT_APP_BACKEND_URL}${o.cover_image}`} alt={o.title} className="w-full h-full object-cover" /> : o.cover_emoji}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-2xl font-extrabold truncate flex items-center gap-2" data-testid="project-title">{o.title}
            <Badge variant="secondary" className="capitalize text-[10px]">{o.status}</Badge>
            {o.quality !== "standard" && <Badge className="gap-1 text-[10px] bg-gradient-to-r from-[#1f6feb] to-[#7c3aed]"><Gem className="w-3 h-3" /> {o.quality}</Badge>}
          </div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] capitalize">{o.template} · {o.dimension} · {o.difficulty} · v{o.version_count} · by {o.owner_name}</div>
        </div>
        {o.is_owner && <Button onClick={() => navigate(`/app/build/${id}`)} variant="outline" className="rounded-full" data-testid="project-edit-btn"><Pencil className="w-4 h-4" /> Open builder</Button>}
        <Button onClick={() => navigate(`/app/arcade/${id}`)} className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" data-testid="project-play-btn"><Play className="w-4 h-4" /> Play</Button>
      </div>

      <div className="grid lg:grid-cols-[200px_1fr] gap-5">
        {/* Section nav */}
        <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible" data-testid="project-nav">
          {SECTIONS.map(([key, label, Icon]) => (
            <button key={key} onClick={() => setSec(key)} data-testid={`project-section-${key}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${sec === key ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]"}`}>
              <Icon className="w-4 h-4 shrink-0" /> {label}
            </button>
          ))}
        </nav>

        {/* Content */}
        <div className="min-w-0" data-testid={`project-content-${sec}`}>
          {sec === "overview" && <Overview p={p} />}
          {sec === "gdd" && <GDD p={p} />}
          {sec === "characters" && <Characters p={p} />}
          {sec === "world" && <WorldView p={p} />}
          {sec === "levels" && <LevelsView p={p} navigate={navigate} id={id} />}
          {sec === "scripts" && <Scripts p={p} />}
          {sec === "audio" && <AudioView p={p} />}
          {sec === "assets" && <AssetsView p={p} />}
          {sec === "economy" && <Economy p={p} />}
          {sec === "exports" && <Exports p={p} navigate={navigate} id={id} />}
          {sec === "saves" && <ProjectSaves p={p} navigate={navigate} id={id} />}
          {sec === "analytics" && <Analytics p={p} />}
        </div>
      </div>
    </div>
  );
}

function Card({ title, children, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mb-4">
      {title && <h2 className="font-display font-bold mb-3 flex items-center gap-2">{Icon && <Icon className="w-5 h-5 text-[hsl(var(--primary))]" />} {title}</h2>}
      {children}
    </div>
  );
}
function Stat({ label, value, icon: Icon, accent = "#3b9eff" }) {
  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs text-[hsl(var(--muted-foreground))]">{label}</span>
        {Icon && <Icon className="w-4 h-4" style={{ color: accent }} />}
      </div>
      <div className="font-display text-2xl font-extrabold">{value}</div>
    </div>
  );
}

function Overview({ p }) {
  const o = p.overview, a = p.analytics;
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Stat label="Plays" value={a.plays} icon={Play} accent="#3b9eff" />
        <Stat label="Likes" value={a.likes} icon={Heart} accent="#ec4899" />
        <Stat label="Rating" value={a.rating ? `★ ${a.rating}` : "—"} icon={Star} accent="#f59e0b" />
        <Stat label="Versions" value={a.versions} icon={Layers} accent="#a855f7" />
      </div>
      <Card title="About this project">
        <p className="text-sm text-[hsl(var(--muted-foreground))]">{o.description || p.gdd.idea || "No description yet."}</p>
        <div className="flex flex-wrap gap-2 mt-3">
          <Badge variant="secondary" className="capitalize">{o.category}</Badge>
          <Badge variant="secondary" className="capitalize">{o.template}</Badge>
          <Badge variant="secondary">{o.dimension.toUpperCase()}</Badge>
          <Badge variant="secondary">Ages {o.age_rating}</Badge>
          <Badge variant="secondary" className="capitalize">{o.difficulty}</Badge>
        </div>
      </Card>
      <Card title="Project contents">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
          <Chip label="Characters" value={p.assets.character_count} />
          <Chip label="Levels" value={p.levels.list.length || (p.levels.has_levels ? "yes" : 0)} />
          <Chip label="Generated assets" value={p.assets.count} />
          <Chip label="Game passes" value={p.economy.passes.length} />
          <Chip label="Community posts" value={a.community_posts} />
          <Chip label="Play sessions" value={a.sessions} />
        </div>
      </Card>
    </div>
  );
}
function Chip({ label, value }) {
  return <div className="flex items-center justify-between rounded-xl border border-[hsl(var(--border))] px-3 py-2"><span className="text-[hsl(var(--muted-foreground))]">{label}</span><span className="font-bold">{value}</span></div>;
}

function GDD({ p }) {
  const plan = p.gdd.plan || {};
  return (
    <div>
      <Card title="Game Design Document" icon={FileText}>
        {p.gdd.idea && <Field label="Original idea">{p.gdd.idea}</Field>}
        {plan.concept && <Field label="Concept">{plan.concept}</Field>}
        {plan.summary && <Field label="Summary">{plan.summary}</Field>}
        {plan.objective && <Field label="Objective">{plan.objective}</Field>}
        {p.gdd.ambience && <Field label="Atmosphere">“{p.gdd.ambience}”</Field>}
        {p.gdd.instructions && <Field label="How to play">{p.gdd.instructions}</Field>}
        {Array.isArray(plan.features) && plan.features.length > 0 && (
          <Field label="Key features"><ul className="list-disc pl-5 space-y-0.5">{plan.features.map((f, i) => <li key={i}>{typeof f === "string" ? f : f.title || JSON.stringify(f)}</li>)}</ul></Field>
        )}
      </Card>
      {p.story && (
        <Card title="Story" icon={FileText}>
          {p.story.title && <div className="font-bold mb-1">{p.story.title}</div>}
          {p.story.intro && <p className="text-sm text-[hsl(var(--muted-foreground))] whitespace-pre-wrap">{p.story.intro}</p>}
          {Array.isArray(p.story.chapters) && <div className="mt-3 space-y-2">{p.story.chapters.map((c, i) => <div key={i} className="text-sm"><span className="font-semibold">{c.title || `Chapter ${i + 1}`}</span>{c.text && <p className="text-[hsl(var(--muted-foreground))]">{c.text}</p>}</div>)}</div>}
        </Card>
      )}
    </div>
  );
}
function Field({ label, children }) {
  return <div className="mb-3"><div className="text-[11px] font-bold text-[hsl(var(--muted-foreground))] uppercase mb-0.5">{label}</div><div className="text-sm">{children}</div></div>;
}

function Characters({ p }) {
  return (
    <Card title="Characters & cast" icon={Users}>
      {p.characters.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]">No characters yet.</p> :
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {p.characters.map((c, i) => (
            <div key={i} className="rounded-xl border border-[hsl(var(--border))] p-3 text-center">
              <div className="aspect-square grid place-items-center mb-1.5 rounded-lg bg-[hsl(var(--secondary))] overflow-hidden">
                {c.image ? <img src={c.image} alt={c.name} className="w-full h-full object-contain p-1" /> : <span className="text-4xl">{c.emoji}</span>}
              </div>
              <div className="font-semibold text-sm capitalize truncate">{c.name}</div>
              <Badge variant="secondary" className="text-[10px] mt-0.5">{c.role}</Badge>
            </div>
          ))}
        </div>}
    </Card>
  );
}

function WorldView({ p }) {
  const w = p.world;
  return (
    <Card title="World & environment" icon={Globe2}>
      <div className="h-24 rounded-xl mb-4" style={{ background: `linear-gradient(135deg, ${w.bgColor || "#0b1026"}, ${w.accent || "#1f6feb"}, ${w.skyColor || "#9AD7FF"})` }} />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
        <Chip label="Theme" value={w.theme || "—"} />
        <Chip label="Biome" value={w.biome || "—"} />
        <Chip label="Time" value={w.timeOfDay || "day"} />
        <Chip label="Weather" value={w.weather || "clear"} />
        <Chip label="Terrain" value={w.terrain || "flat"} />
        <Chip label="Quality" value={w.quality || "standard"} />
        <Chip label="World size" value={w.worldSize || "—"} />
        <Chip label="Density" value={w.density || "normal"} />
      </div>
      {w.ambience && <p className="text-sm italic text-[hsl(var(--muted-foreground))] mt-3">“{w.ambience}”</p>}
    </Card>
  );
}

function LevelsView({ p, navigate, id }) {
  const l = p.levels;
  return (
    <Card title="Levels & campaign" icon={Layers}>
      {!l.has_levels || l.list.length === 0 ? (
        <EmptyState emoji="🗺️" title="No levels yet" text="Generate a level campaign from the builder's Levels tool." action={p.overview.is_owner ? <Button onClick={() => navigate(`/app/levels/${id}`)} className="rounded-full">Build levels</Button> : null} />
      ) : (
        <div className="space-y-1.5">
          {l.list.map((lv, i) => (
            <div key={i} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] px-3 py-2 text-sm">
              <span className="w-7 h-7 rounded-lg grid place-items-center bg-[hsl(var(--secondary))] font-bold text-xs">{(lv.index ?? i) + 1}</span>
              <span className="flex-1 truncate">{lv.name || `Level ${i + 1}`}</span>
              {lv.world_id && <Badge variant="secondary" className="text-[10px] capitalize">{lv.world_id}</Badge>}
              {lv.boss && <Badge className="text-[10px] bg-red-500/80">Boss</Badge>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function Scripts({ p }) {
  const s = p.scripts;
  return (
    <Card title="Game logic & scripts" icon={Code2}>
      <Field label="Objective">{s.goal?.type === "score" ? `Reach ${s.goal.target} points` : s.goal?.type === "reach" ? "Reach the goal zone" : `${s.goal?.type || "Complete"} — target ${s.goal?.target || "?"}`}</Field>
      <Field label="Controls">{s.controls}</Field>
      <Field label="Core mechanics">
        {s.mechanics.length === 0 ? "—" : <ul className="space-y-1">{s.mechanics.map((m, i) => <li key={i} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> {m}</li>)}</ul>}
      </Field>
    </Card>
  );
}

function AudioView({ p }) {
  return (
    <Card title="Audio & narration" icon={Music}>
      <div className="space-y-2 text-sm">
        <Row ok={!!p.audio.ambience} label="Ambience / mood" detail={p.audio.ambience || "Not set"} />
        <Row ok={!!p.audio.narration} label="Story narration" detail={p.audio.narration ? "Story intro available (text-to-speech ready)" : "No narration yet"} />
      </div>
      <p className="text-xs text-[hsl(var(--muted-foreground))] mt-3">Tip: generate voiceovers from the builder (Story → narrate) and sound packs from the Marketplace.</p>
    </Card>
  );
}
function Row({ ok, label, detail }) {
  return <div className="flex items-start gap-2">{ok ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" /> : <Circle className="w-4 h-4 text-[hsl(var(--muted-foreground))] mt-0.5" />}<div><span className="font-semibold">{label}</span><div className="text-[hsl(var(--muted-foreground))] text-xs">{detail}</div></div></div>;
}

function AssetsView({ p }) {
  const a = p.assets.generated || [];
  return (
    <Card title="Generated assets" icon={Boxes}>
      {a.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]">No generated art assets stored for this game yet. Generate character art or 3D models from the builder.</p> :
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {a.map((as, i) => (
            <div key={i} className="rounded-xl border border-[hsl(var(--border))] p-2 text-center">
              <div className="aspect-square grid place-items-center rounded-lg bg-[hsl(var(--secondary))] mb-1 overflow-hidden">
                {as.url || as.image ? <img src={as.url || as.image} alt={as.name} className="w-full h-full object-contain" /> : <Boxes className="w-6 h-6 text-[hsl(var(--muted-foreground))]" />}
              </div>
              <div className="text-[11px] truncate">{as.name || "Asset"}</div>
            </div>
          ))}
        </div>}
    </Card>
  );
}

function Economy({ p }) {
  const e = p.economy;
  return (
    <div>
      <Card title="Monetisation" icon={Coins}>
        {e.monetization.enabled ? (
          <div className="flex flex-wrap gap-2"><Badge className="bg-emerald-500/80">Premium · {e.monetization.price} credits</Badge><Badge variant="secondary" className="capitalize">{e.monetization.status}</Badge></div>
        ) : <p className="text-sm text-[hsl(var(--muted-foreground))]">This game is free to play. Enable premium pricing or sell passes from the builder.</p>}
      </Card>
      <Card title="Game passes & items" icon={Coins}>
        {e.passes.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]">No passes yet.</p> :
          <div className="space-y-2">{e.passes.map((pa, i) => (
            <div key={i} className="flex items-center justify-between rounded-xl border border-[hsl(var(--border))] px-3 py-2 text-sm"><span>{pa.title}</span><div className="flex items-center gap-2"><Badge variant="secondary">{pa.price} cr</Badge>{pa.active ? <Badge className="bg-emerald-500/80 text-[10px]">Active</Badge> : <Badge variant="secondary" className="text-[10px]">Draft</Badge>}</div></div>
          ))}</div>}
      </Card>
    </div>
  );
}

function CinematicStreamCard({ id }) {
  const [url, setUrl] = useState("");
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/cinematic/config?game_id=${encodeURIComponent(id)}`)
      .then(({ data }) => { if (data.per_game) { setUrl(data.signalling_url || ""); setSaved(data.signalling_url || ""); } })
      .catch(() => {});
  }, [id]);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/games/${id}/cinematic`, { cinematic_url: url.trim() });
      setSaved(data.cinematic_url || "");
      toast.success(data.enabled ? "Cinematic stream URL saved for this game! 🎬" : "Cleared — this game will use the global Cinematic stream.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save the stream URL"); }
    finally { setBusy(false); }
  };
  return (
    <Card title="Cinematic Mode stream (per-game)" icon={Clapperboard}>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
        Point this game at your own packaged Unreal <b>Pixel Streaming</b> URL. When set, players who open
        <b> Cinematic Mode</b> stream <i>this</i> game — overriding the platform's global stream.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="https://connector.eagle3dstreaming.com/v5/…"
          data-testid="cinematic-url-input"
          className="flex-1 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-4 py-2 text-sm outline-none focus:border-[hsl(var(--primary))]/60"
        />
        <Button onClick={save} disabled={busy} className="rounded-full gap-1.5 bg-gradient-to-r from-amber-400 to-orange-500 text-black" data-testid="cinematic-url-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </Button>
      </div>
      {saved
        ? <p className="text-[11px] text-emerald-500 font-semibold mt-2" data-testid="cinematic-url-status">✓ This game streams its own Unreal build.</p>
        : <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2" data-testid="cinematic-url-status">Using the global Cinematic stream (if the platform has one enabled).</p>}
    </Card>
  );
}

function Exports({ p, navigate, id }) {
  const [ec, setEc] = useState(null);
  const [busy, setBusy] = useState("");
  const [models, setModels] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [polling, setPolling] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;
  const isOwner = p.overview.is_owner;
  const is3D = (p.overview.dimension || "").includes("3d") || ["kart3d", "racing3d", "fps3d", "tower3d", "explorer3d"].includes(p.overview.template);

  useEffect(() => { if (isOwner) api.get(`/bridge/export-center/${id}`).then((r) => setEc(r.data)).catch(() => setEc(false)); }, [id, isOwner]);

  const refreshModels = async () => {
    try { const { data } = await api.get(`/bridge/models-status/${id}`); setModels(data.models || []); return data.all_done; }
    catch { return true; }
  };
  useEffect(() => { if (isOwner) refreshModels(); }, [id, isOwner]); // eslint-disable-line
  useEffect(() => {
    if (!polling) return;
    const t = setInterval(async () => { const done = await refreshModels(); if (done) setPolling(false); }, 5000);
    return () => clearInterval(t);
  }, [polling]); // eslint-disable-line

  if (!isOwner) return <Card title="Export & connectors" icon={Download}><p className="text-sm text-[hsl(var(--muted-foreground))]">Only the creator can export this project.</p></Card>;

  const dl = (url, filename) => {
    const a = document.createElement("a"); a.href = url.startsWith("http") ? url : `${API}${url}`;
    if (filename) a.download = filename; a.target = "_blank"; a.rel = "noopener"; document.body.appendChild(a); a.click(); a.remove();
  };
  const generateModels = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post(`/bridge/generate-models/${id}`, { art_style: "realistic" });
      if (data.count > 0) { toast.success(`Generating ${data.count} real 3D model${data.count > 1 ? "s" : ""}… this takes a few minutes. ✨`); setPolling(true); refreshModels(); }
      else toast.info("All characters already have 3D models — or there are none to generate.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start 3D generation"); }
    finally { setGenBusy(false); }
  };
  const exportEngine = async (engine) => {
    setBusy(engine);
    try { const { data } = await api.post(`/bridge/export/${id}`, { engine }); toast.success(`${engine} package ready!`); dl(data.url, data.filename); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed"); }
    finally { setBusy(""); }
  };
  const exportWeb = async () => {
    setBusy("web");
    try { const { data } = await api.post(`/games/${id}/export-web`); toast.success("Web build ready!"); dl(data.url, data.filename); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed"); }
    finally { setBusy(""); }
  };
  const exportJson = () => {
    const blob = new Blob([JSON.stringify({ overview: p.overview, gdd: p.gdd, world: p.world, characters: p.characters, levels: p.levels, scripts: p.scripts, economy: p.economy }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob); dl(url, `${p.overview.title.replace(/\s+/g, "_")}.json`); setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const readyCount = (models || []).filter((m) => m.status === "succeeded").length;

  return (
    <div data-testid="export-center">
      {is3D && (
        <Card title="AI 3D models (Meshy text-to-3D)" icon={Wand2}>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
            Turn every character in your game into a real <b>.glb</b> 3D model. They render live in-browser and are bundled into your Unity / Unreal / Blender exports automatically.
          </p>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Button onClick={generateModels} disabled={genBusy || polling} className="rounded-full bg-gradient-to-r from-[#7c3aed] to-[#3b9eff]" data-testid="generate-3d-btn">
              {genBusy || polling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {polling ? "Generating…" : "Generate 3D models"}
            </Button>
            <Button variant="outline" size="sm" onClick={refreshModels} className="rounded-full" data-testid="refresh-3d-btn"><RefreshCw className="w-4 h-4" /> Refresh</Button>
            {readyCount > 0 && <span className="text-xs text-emerald-500 font-semibold">{readyCount} ready</span>}
          </div>
          {models && models.length > 0 && (
            <div className="space-y-2" data-testid="gen-3d-list">
              {models.map((m) => (
                <div key={m.asset_id} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] p-2.5" data-testid={`gen-3d-${m.asset_id}`}>
                  <div className="w-9 h-9 rounded-lg bg-[hsl(var(--secondary))] overflow-hidden grid place-items-center shrink-0">{m.thumbnail ? <img src={m.thumbnail} alt={m.name} className="w-full h-full object-cover" /> : <Boxes className="w-4 h-4" />}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{m.name} <span className="text-[10px] uppercase text-[hsl(var(--muted-foreground))]">{m.kind}</span></div>
                    {m.status === "succeeded" ? <div className="text-[11px] text-emerald-500 font-semibold">✓ Ready — used in-game & exports</div>
                      : m.status === "failed" ? <div className="text-[11px] text-[hsl(var(--destructive))]">Generation failed — try again</div>
                        : <div className="mt-1 h-1.5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden"><div className="h-full bg-gradient-to-r from-[#7c3aed] to-[#3b9eff] transition-all" style={{ width: `${m.progress || 4}%` }} /></div>}
                  </div>
                  {(m.status !== "succeeded" && m.status !== "failed") && <Loader2 className="w-4 h-4 animate-spin text-[hsl(var(--primary))]" />}
                </div>
              ))}
            </div>
          )}
          {(!models || models.length === 0) && <p className="text-xs text-[hsl(var(--muted-foreground))]">No 3D models yet. Click <b>Generate 3D models</b> to create them from your characters.</p>}
        </Card>
      )}

      <Card title="Game engine connectors" icon={Rocket}>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">Take your project to a professional engine. We export an editable project — no copied engine assets.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {(ec?.engines || [{ key: "unity", label: "Unity", desc: "C# + scene (.zip)" }, { key: "unreal", label: "Unreal Engine", desc: "Scene + manifest (.zip)" }, { key: "blender", label: "Blender", desc: "bpy script + scene.json (.zip)" }]).map((eng) => (
            <button key={eng.key} onClick={() => exportEngine(eng.key)} disabled={!!busy} data-testid={`export-${eng.key}`}
              className="text-left rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 hover:border-[hsl(var(--primary))]/60 hover:-translate-y-0.5 transition-all disabled:opacity-50">
              <div className="flex items-center justify-between mb-1.5"><span className="font-bold">{eng.label}</span>{busy === eng.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 text-[hsl(var(--primary))]" />}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{eng.desc}</div>
            </button>
          ))}
        </div>
      </Card>

      <Card title="Playable & data exports" icon={Download}>
        <div className="grid sm:grid-cols-2 gap-3">
          <button onClick={exportWeb} disabled={!!busy} data-testid="export-web" className="text-left rounded-xl border border-[hsl(var(--border))] p-4 hover:border-[hsl(var(--primary))]/60 transition-all disabled:opacity-50">
            <div className="flex items-center justify-between mb-1.5"><span className="font-bold">Web build</span>{busy === "web" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 text-[hsl(var(--primary))]" />}</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">Standalone playable HTML (.zip)</div>
          </button>
          <button onClick={exportJson} data-testid="export-json" className="text-left rounded-xl border border-[hsl(var(--border))] p-4 hover:border-[hsl(var(--primary))]/60 transition-all">
            <div className="flex items-center justify-between mb-1.5"><span className="font-bold">Project JSON</span><Download className="w-4 h-4 text-[hsl(var(--primary))]" /></div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">Full project + config data (.json)</div>
          </button>
        </div>
      </Card>

      <Card title="3D asset exports (GLB / FBX / OBJ / USDZ)" icon={Boxes}>
        {ec === false ? <p className="text-sm text-[hsl(var(--muted-foreground))]">Couldn't load assets.</p> :
          !ec ? <Loader2 className="w-5 h-5 animate-spin" /> :
            ec.assets.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]">No 3D models yet. Generate models in the <button onClick={() => navigate("/app/3d")} className="text-[hsl(var(--primary))] underline">3D Studio</button> — they'll appear here with GLB/FBX/OBJ/USDZ downloads.</p> :
              <div className="space-y-2" data-testid="export-3d-assets">
                {ec.assets.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] p-3">
                    <div className="w-10 h-10 rounded-lg bg-[hsl(var(--secondary))] overflow-hidden grid place-items-center shrink-0">{a.thumbnail ? <img src={a.thumbnail} alt={a.name} className="w-full h-full object-cover" /> : <Boxes className="w-5 h-5" />}</div>
                    <span className="flex-1 truncate text-sm font-medium">{a.name}</span>
                    <div className="flex gap-1.5">
                      {Object.entries(a.formats).map(([fmt, url]) => (
                        <Button key={fmt} size="sm" variant="outline" className="rounded-full uppercase text-[10px] h-7" onClick={() => dl(url, `${a.name}.${fmt}`)} data-testid={`dl-${a.id}-${fmt}`}>{fmt}</Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>}
      </Card>

      <LicenceProvenanceCard gameId={id} />
      <CinematicStreamCard id={id} />
    </div>
  );
}

function ProjectSaves({ p, navigate, id }) {
  const [snaps, setSnaps] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState("");
  const isOwner = p.overview.is_owner;
  const API = process.env.REACT_APP_BACKEND_URL;

  const load = () => api.get(`/projects/${id}/snapshots`).then((r) => setSnaps(r.data.snapshots || [])).catch(() => setSnaps([]));
  useEffect(() => { if (isOwner) load(); }, [id, isOwner]); // eslint-disable-line

  if (!isOwner) return <Card title="Project & Saves" icon={Save}><p className="text-sm text-[hsl(var(--muted-foreground))]">Only the creator can manage project saves.</p></Card>;

  const saveSnap = async () => {
    const nm = name.trim() || `Snapshot ${new Date().toLocaleString()}`;
    setBusy("save");
    try { await api.post(`/projects/${id}/snapshots`, { name: nm }); toast.success("Snapshot saved! 💾"); setName(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const restore = async (s) => {
    if (!window.confirm(`Restore "${s.name}"? This replaces the current config and ALL levels of this project.`)) return;
    setBusy(`r-${s.id}`);
    try { const { data } = await api.post(`/projects/${id}/snapshots/${s.id}/restore`); toast.success(`Restored — ${data.level_count} levels loaded.`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const del = async (s) => {
    setBusy(`d-${s.id}`);
    try { await api.delete(`/projects/${id}/snapshots/${s.id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const duplicate = async () => {
    setBusy("dup");
    try { const { data } = await api.post(`/projects/${id}/duplicate`, {}); toast.success(`Cloned with ${data.level_count} levels!`); navigate(`/app/project/${data.id}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const exportBundle = async () => {
    setBusy("export");
    try {
      const { data } = await api.get(`/projects/${id}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob); const a = document.createElement("a");
      a.href = url; a.download = `${p.overview.title.replace(/\s+/g, "_")}.gvproject.json`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Project bundle exported!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="project-saves">
      <Card title="Save states (snapshots)" icon={History}>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">Capture the entire project — config, story, quests, economy and <b>all levels</b> — then restore it anytime. Keeps your last 20 saves.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Snapshot name (optional)" className="flex-1 min-w-[180px] rounded-lg border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm" data-testid="snapshot-name" />
          <Button onClick={saveSnap} disabled={busy === "save"} className="rounded-full" data-testid="save-snapshot-btn">{busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save snapshot</Button>
        </div>
        {snaps === null ? <Loader2 className="w-5 h-5 animate-spin text-[hsl(var(--primary))]" /> : snaps.length === 0 ? (
          <p className="text-xs text-[hsl(var(--muted-foreground))]">No saves yet. Create one before big changes so you can roll back safely.</p>
        ) : (
          <div className="space-y-2" data-testid="snapshot-list">
            {snaps.map((s) => (
              <div key={s.id} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] p-2.5" data-testid={`snapshot-${s.id}`}>
                <Save className="w-4 h-4 text-[hsl(var(--primary))] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{s.name}</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{new Date(s.created_at).toLocaleString()} · {s.level_count} levels</div>
                </div>
                <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => restore(s)} disabled={busy === `r-${s.id}`} data-testid={`restore-${s.id}`}>{busy === `r-${s.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Restore</Button>
                <button onClick={() => del(s)} disabled={busy === `d-${s.id}`} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]" data-testid={`delete-snapshot-${s.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Clone & transfer" icon={Copy}>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">Duplicate this whole project into a new draft, export it as a portable bundle, or publish it as a community template others can clone.</p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={duplicate} disabled={busy === "dup"} variant="outline" className="rounded-full" data-testid="duplicate-project-btn">{busy === "dup" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />} Duplicate project</Button>
          <Button onClick={exportBundle} disabled={busy === "export"} variant="outline" className="rounded-full" data-testid="export-bundle-btn">{busy === "export" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export bundle (.json)</Button>
          <PublishTemplateDialog gameId={id} gameTitle={p.overview.title} defaultCategory={p.overview.category} onPublished={() => navigate("/app/community-templates")} />
        </div>
      </Card>
    </div>
  );
}


function Analytics({ p }) {
  const a = p.analytics;
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <Stat label="Total plays" value={a.plays} icon={Play} />
        <Stat label="Play sessions" value={a.sessions} icon={BarChart3} accent="#06b6d4" />
        <Stat label="Likes" value={a.likes} icon={Heart} accent="#ec4899" />
        <Stat label="Avg rating" value={a.rating ? `★ ${a.rating}` : "—"} icon={Star} accent="#f59e0b" />
        <Stat label="Ratings" value={a.rating_count} icon={Star} accent="#eab308" />
        <Stat label="Community posts" value={a.community_posts} icon={Users} accent="#a855f7" />
      </div>
      <Card><p className="text-sm text-[hsl(var(--muted-foreground))]">Deeper engagement analytics (retention, drop-off, revenue) live in the Creator Dashboard.</p></Card>
    </div>
  );
}
